import { useEffect, useState } from "react";
import { storage, deviceDetect } from "../utils";

// Lightweight device-capability detection for the LEGACY login screen, ported from the
// mobile app's useDeviceCaps (ClientAppMobile/src/hooks/useDeviceCaps.ts). The detection
// constants are kept byte-identical so the two SPAs classify the same hardware the same way.
//
// Purpose: auto-surface the badge/QR login link on a scanner handheld and route it to the
// mobile experience WITHOUT the post-login device picker. Returns a reactive `hasScanner`
// boolean held in local React state — a raw localStorage write would NOT re-render the login
// screen (useLocalStorage has no storage-event listener). On confident scanner detection the
// device profile is persisted through the context SETTERS (setSavedDevice/setOnMobile), not a
// raw setItem, so App-level state updates live and post-login routing follows.
//
// UX only, never a security boundary (User-Agent is spoofable) — the badge-token gate and the
// network gate enforce access. Camera detection is intentionally NOT done here: login skips the
// async device enumeration the mobile app does, so it would only be a guess. The mobile app and
// the picker auto-classifier own the camera/scanner input decision.

const RUGGED_UA = /unitech|zebra|honeywell|datalogic|symbol|m3\s*mobile|cilico/i;

// Wedge burst signature (verbatim from useDeviceCaps): a hardware imager injects keys far
// faster than a human types. At least WEDGE_MIN_KEYS within an average inter-key interval
// below WEDGE_MAX_AVG_INTERVAL_MS, terminated by Enter.
const WEDGE_MIN_KEYS = 6;
const WEDGE_MAX_AVG_INTERVAL_MS = 30;
const WEDGE_BUFFER_SIZE = 12;

// The DEVICE_TYPES "Handheld Scanner" profile (constants.js) — inlined to avoid the dependency.
const HANDHELD_SCANNER = { label: "Handheld Scanner", value: 1, isMobile: true, hasScanner: true };

function deviceTypeHasScanner() {
   // deviceType is JSON. JSON.parse(null) -> null (safe), but JSON.parse("undefined") throws —
   // guard both, then optional-chain, mirroring SignIn.js / UserProfile.js.
   const raw = storage.getItem("deviceType");
   if (!raw || raw === "undefined") return false;
   try {
      const parsed = JSON.parse(raw);
      return Boolean(parsed?.hasScanner);
   }
   catch {
      return false;
   }
}

export default function useLoginDeviceDetect(userCtx) {
   const [hasScanner, setHasScanner] = useState(() =>
      // An iPhone/iPod is authoritatively camera-only — never a scanner handheld — so ignore a
      // stale wedge flag or poisoned deviceType for Apple phones (mirrors useDeviceCaps).
      !deviceDetect.isNoScannerMobile()
      && (RUGGED_UA.test(navigator.userAgent)
         || storage.getItem("wedgeDetected") === "true"
         || deviceTypeHasScanner())
   );

   // Once a scanner is (or becomes) known, pin the Handheld Scanner profile so post-login
   // routing goes mobile and the picker is bypassed. Idempotent — skip if already pinned.
   useEffect(() => {
      if (!hasScanner || deviceTypeHasScanner()) return;
      userCtx.setSavedDevice(HANDHELD_SCANNER);
      userCtx.setOnMobile(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [hasScanner]);

   // Document-level wedge watcher, installed only while no scanner is known. A global listener
   // catches the burst regardless of which field has focus, then self-detaches on detection.
   useEffect(() => {
      if (hasScanner || deviceDetect.isNoScannerMobile()) return;
      const stamps = [];
      function handleKeyDown(e) {
         const now = performance.now();
         stamps.push(now);
         if (stamps.length > WEDGE_BUFFER_SIZE) stamps.shift();
         if (e.key !== "Enter" || stamps.length < WEDGE_MIN_KEYS) return;
         const span = stamps[stamps.length - 1] - stamps[0];
         const avgInterval = span / (stamps.length - 1);
         stamps.length = 0;
         if (avgInterval >= WEDGE_MAX_AVG_INTERVAL_MS) return; // human typing — ignore
         storage.setItem("wedgeDetected", "true");
         setHasScanner(true);
      }
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
   }, [hasScanner]);

   return hasScanner;
}
